import { ButtonHTMLAttributes, ReactNode, forwardRef, useState } from 'react';

export type TextButtonTone = 'accent' | 'primary' | 'secondary' | 'muted';

const TONE_COLOR: Record<TextButtonTone, string> = {
  accent: 'var(--accent)',
  primary: 'var(--text-primary)',
  secondary: 'var(--text-secondary)',
  muted: 'var(--text-muted)',
};

export interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Text color token. Default `accent`. */
  tone?: TextButtonTone;
  children: ReactNode;
}

/**
 * Chrome-less text button — a `<button>` styled as an inline link (no padding,
 * background, or border; underline on hover). For click-to-act text such as
 * breadcrumb segments and inline value-edit triggers, where a full `Button`'s
 * box would be visually wrong. (`ButtonLink` is the `<a>` sibling for real
 * `href` navigation; this is for `onClick` actions that aren't navigations.)
 * Font sizing/family stay with the caller via `className`.
 */
export const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(
  function TextButton({ tone = 'accent', className, style, disabled, children, ...rest }, ref) {
    const [hover, setHover] = useState(false);
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={className}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: TONE_COLOR[tone],
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          textDecoration: hover && !disabled ? 'underline' : 'none',
          transition: 'color 0.15s ease, opacity 0.15s ease',
          ...style,
        }}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
